import React from "react";

// ─────────────────────────────────────────────────────────────────
// STYLE SYSTEM
// ─────────────────────────────────────────────────────────────────
const S = {
  wrapper:      { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Definition / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Bracket helper
function Bracket({ x, y, h, side, color='#1F3864', w=10 }) {
  if (side === 'left')  return <path d={`M ${x+w},${y} L ${x},${y} L ${x},${y+h} L ${x+w},${y+h}`} fill="none" stroke={color} strokeWidth="2"/>;
  if (side === 'right') return <path d={`M ${x-w},${y} L ${x},${y} L ${x},${y+h} L ${x-w},${y+h}`} fill="none" stroke={color} strokeWidth="2"/>;
  return null;
}

// Compact SVG matrix renderer
function Mat({ data, x0, y0, cW=46, cH=34, colors=null, textColors=null, color='#1F3864' }) {
  const rows = data.length, cols = data[0].length;
  const W = cols * cW, H = rows * cH;
  return (
    <g>
      <Bracket x={x0-4} y={y0-4} h={H+8} side="left"  color={color}/>
      <Bracket x={x0+W+4} y={y0-4} h={H+8} side="right" color={color}/>
      {data.map((row, r) => row.map((val, c) => {
        const cx = x0 + c*cW + cW/2;
        const cy = y0 + r*cH + cH/2 + 5;
        const bg = colors && colors[r] && colors[r][c] ? colors[r][c] : null;
        const fc = textColors && textColors[r] && textColors[r][c] ? textColors[r][c] : '#1F3864';
        return (
          <g key={`${r}-${c}`}>
            {bg && <rect x={x0+c*cW+2} y={y0+r*cH+2} width={cW-4} height={cH-4} rx={3} fill={bg} opacity="0.65"/>}
            <text x={cx} y={cy} textAnchor="middle" fontSize="13"
              fill={fc} fontFamily="'Courier New', monospace" fontWeight={bg ? '700' : '400'}>
              {val}
            </text>
          </g>
        );
      }))}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Transpose: show A and Aᵀ side by side with color-coded flip
// ─────────────────────────────────────────────────────────────────
function DiagramTranspose() {
  const W = 880, H = 260;
  // A is 3×4, Aᵀ is 4×3 — asymmetric to make the flip obvious
  const A = [
    ['1','2','3','4'],
    ['5','6','7','8'],
    ['9','10','11','12'],
  ];
  const AT = [
    ['1','5','9'],
    ['2','6','10'],
    ['3','7','11'],
    ['4','8','12'],
  ];
  // Color each element by its original row in A
  const rowColors = ['#d0e8ff','#ffd0d0','#d4f0d4','#fff3cc'];
  const colorA  = A.map((row,r)  => row.map(()  => rowColors[r]));
  const colorAT = AT.map((row,r) => row.map(()  => rowColors[r]));

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Transpose — Rows Become Columns, Columns Become Rows</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Label A */}
        <text x={60} y={30} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">A  (3×4)</text>
        <Mat data={A} x0={10} y0={42} cW={52} cH={38} colors={colorA}/>

        {/* Arrow */}
        <text x={310} y={128} textAnchor="middle" fontSize="22" fill="#2E75B6">→</text>
        <text x={310} y={148} textAnchor="middle" fontSize="12" fill="#2E75B6" fontFamily="'Courier New',monospace">transpose</text>

        {/* Label Aᵀ */}
        <text x={530} y={30} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Aᵀ  (4×3)</text>
        <Mat data={AT} x0={360} y0={42} cW={52} cH={38} colors={colorAT}/>

        {/* Color legend — spread across bottom */}
        {['Row 1 → Col 1','Row 2 → Col 2','Row 3 → Col 3'].map((lbl,i) => (
          <g key={i}>
            <rect x={540 + i*110} y={200} width={16} height={14} rx={2} fill={rowColors[i]} stroke="#bbb" strokeWidth="1"/>
            <text x={562 + i*110} y={212} fontSize="11" fill="#555">{lbl}</text>
          </g>
        ))}

        {/* Properties box */}
        <rect x={10} y={196} width={490} height={56} rx={5} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={255} y={214} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Key Properties</text>
        <text x={255} y={230} textAnchor="middle" fontSize="11.5" fill="#2E75B6" fontFamily="'Courier New',monospace">(Aᵀ)ᵀ = A     (AB)ᵀ = BᵀAᵀ     (A+B)ᵀ = Aᵀ+Bᵀ</text>
        <text x={255} y={246} textAnchor="middle" fontSize="11" fill="#555">
          Symmetric matrix: A = Aᵀ  (stiffness matrices in FEA are always symmetric)
        </text>
      </svg>
      <div style={S.dCaption}>
        Each row of A becomes the corresponding column of Aᵀ. Color shows where each element ends up. The (AB)ᵀ = BᵀAᵀ reversal rule is a common source of errors — order flips on transpose.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Rank and Row Reduction
// Show a 3×3 matrix being reduced to row echelon form, with rank annotation
// ─────────────────────────────────────────────────────────────────
function DiagramRank() {
  const W = 880, H = 290;

  const step0 = [['1','2','3'],['2','4','6'],['1','3','5']];
  const step1 = [['1','2','3'],['0','0','0'],['0','1','2']];
  const step2 = [['1','2','3'],['0','1','2'],['0','0','0']];

  // Highlight pivot positions
  const pivotColors0 = [[null,null,null],[null,null,null],[null,null,null]];
  const pivotColors2 = [
    ['#d0e8ff',null,null],
    [null,'#d0e8ff',null],
    [null,null,null],
  ];

  const steps = [
    { data: step0, label: 'Original Matrix', colors: pivotColors0, note: 'Row 2 = 2×Row 1  →  dependent' },
    { data: step1, label: 'After R2 → R2−2R1', colors: pivotColors0, note: 'Zero row appears' },
    { data: step2, label: 'Row Echelon Form', colors: pivotColors2, note: 'Rank = 2  (two pivot rows)' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Rank via Row Reduction — Counting Independent Rows</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {steps.map((st, si) => {
          const px = 20 + si * 295;
          return (
            <g key={si}>
              {/* Step box */}
              <rect x={px} y={8} width={270} height={270} rx={6}
                fill="#fff" stroke={si===2 ? "#27ae60" : "#2E75B6"} strokeWidth="1.6"/>

              {/* Step label */}
              <text x={px+135} y={26} textAnchor="middle" fontSize="12.5"
                fill={si===2 ? "#27ae60" : "#1F3864"} fontWeight="700">{`Step ${si+1}: ${st.label}`}</text>

              {/* Matrix — centered in panel */}
              <Mat data={st.data} x0={px+60} y0={44} cW={50} cH={40} colors={st.colors}/>

              {/* Annotations for zero row (steps 1 and 2) */}
              {si === 1 && (
                <g>
                  <rect x={px+60} y={84+40} width={150} height={38} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
                  <text x={px+135} y={106} textAnchor="middle" fontSize="11.5" fill="#c0392b" fontWeight="700">zero row</text>
                  <text x={px+135} y={120} textAnchor="middle" fontSize="10.5" fill="#c0392b">dependent — carries no info</text>
                </g>
              )}
              {si === 2 && (
                <g>
                  {/* pivot markers */}
                  <text x={px+58} y={72} textAnchor="end" fontSize="20" fill="#2E75B6">↑</text>
                  <text x={px+50} y={68} textAnchor="end" fontSize="11" fill="#2E75B6">pivot</text>
                  <text x={px+110} y={115} textAnchor="end" fontSize="20" fill="#2E75B6">↑</text>
                  <text x={px+108} y={111} textAnchor="end" fontSize="11" fill="#2E75B6">pivot</text>
                </g>
              )}

              {/* Note at bottom */}
              <rect x={px+10} y={216} width={250} height={44} rx={4}
                fill={si===2 ? "#f0fff4" : "#f8fbff"} stroke={si===2 ? "#27ae60" : "#ccd9ea"} strokeWidth="1"/>
              <text x={px+135} y={234} textAnchor="middle" fontSize="11.5"
                fill={si===2 ? "#27ae60" : "#555"} fontWeight={si===2 ? "700" : "400"}>{st.note}</text>
              {si===2 && (
                <text x={px+135} y={252} textAnchor="middle" fontSize="11" fill="#555">
                  rank(A) = 2  &lt;  3  →  singular
                </text>
              )}

              {/* Arrow between panels */}
              {si < 2 && (
                <text x={px+282} y={140} textAnchor="middle" fontSize="22" fill="#888">→</text>
              )}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Row reduction (Gaussian elimination) strips out dependent rows by subtracting multiples. The number of non-zero rows remaining is the rank. Here rank = 2 because row 2 was simply 2× row 1 — it contained no new information. A full-rank 3×3 matrix would have rank 3 and be invertible.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Four Fundamental Subspaces of a Matrix
// ─────────────────────────────────────────────────────────────────
function DiagramFourSubspaces() {
  const W = 880, H = 310;

  const subspaces = [
    {
      name: 'Column Space',
      symbol: 'C(A)',
      color: '#2E75B6',
      bg: '#d0e8ff',
      dim: 'dim = rank(A)',
      desc: 'All possible outputs Ax',
      note: 'Span of columns of A',
      x: 10,
    },
    {
      name: 'Null Space',
      symbol: 'N(A)',
      color: '#c0392b',
      bg: '#ffd0d0',
      dim: 'dim = n − rank(A)',
      desc: 'All x where Ax = 0',
      note: 'Directions A collapses to zero',
      x: 230,
    },
    {
      name: 'Row Space',
      symbol: 'C(Aᵀ)',
      color: '#27ae60',
      bg: '#d4f0d4',
      dim: 'dim = rank(A)',
      desc: 'All possible outputs Aᵀy',
      note: 'Span of rows of A',
      x: 450,
    },
    {
      name: 'Left Null Space',
      symbol: 'N(Aᵀ)',
      color: '#9b59b6',
      bg: '#f0e0ff',
      dim: 'dim = m − rank(A)',
      desc: 'All y where Aᵀy = 0',
      note: 'Orthogonal complement of C(A)',
      x: 670,
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Four Fundamental Subspaces of a Matrix A (m×n)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {subspaces.map((sp, i) => (
          <g key={i}>
            <rect x={sp.x} y={8} width={205} height={282} rx={7}
              fill="#fff" stroke={sp.color} strokeWidth="2"/>

            {/* Header */}
            <rect x={sp.x} y={8} width={205} height={44} rx={7} fill={sp.bg}/>
            <rect x={sp.x} y={36} width={205} height={16} fill={sp.bg}/>
            <text x={sp.x+102} y={25} textAnchor="middle" fontSize="14"
              fill={sp.color} fontWeight="700">{sp.name}</text>
            <text x={sp.x+102} y={42} textAnchor="middle" fontSize="13"
              fill={sp.color} fontFamily="'Courier New',monospace">{sp.symbol}</text>

            {/* Dimension */}
            <rect x={sp.x+10} y={62} width={185} height={34} rx={4} fill={sp.bg} opacity="0.5"/>
            <text x={sp.x+102} y={80} textAnchor="middle" fontSize="12"
              fill={sp.color} fontWeight="700">Dimension</text>
            <text x={sp.x+102} y={96} textAnchor="middle" fontSize="12"
              fill={sp.color} fontFamily="'Courier New',monospace" fontWeight="700">{sp.dim}</text>

            {/* Description */}
            <text x={sp.x+102} y={122} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">What it is:</text>
            <text x={sp.x+102} y={138} textAnchor="middle" fontSize="11.5" fill="#555">{sp.desc}</text>

            {/* Note */}
            <text x={sp.x+102} y={162} textAnchor="middle" fontSize="11.5" fill="#555">{sp.note}</text>

            {/* Divider */}
            <line x1={sp.x+10} y1={175} x2={sp.x+195} y2={175} stroke="#eee" strokeWidth="1"/>

            {/* Lives in label */}
            <text x={sp.x+102} y={194} textAnchor="middle" fontSize="11.5" fill="#888">Lives in:</text>
            <text x={sp.x+102} y={212} textAnchor="middle" fontSize="13"
              fill={sp.color} fontFamily="'Courier New',monospace" fontWeight="700">
              {i < 2 ? 'ℝᵐ  (output space)' : 'ℝⁿ  (input space)'}
            </text>

            {/* Orthogonality note */}
            <rect x={sp.x+10} y={228} width={185} height={50} rx={4} fill="#f8fbff" stroke="#ccd9ea" strokeWidth="1"/>
            <text x={sp.x+102} y={246} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Orthogonal partner:</text>
            <text x={sp.x+102} y={262} textAnchor="middle" fontSize="11.5" fill={sp.color} fontFamily="'Courier New',monospace">
              {['N(Aᵀ)','C(A)','N(A)','C(Aᵀ)'][i]}
            </text>
            <text x={sp.x+102} y={274} textAnchor="middle" fontSize="10.5" fill="#888">
              {['⊥ left null space','⊥ column space','⊥ row space','⊥ null space'][i]}
            </text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Every matrix has exactly four fundamental subspaces. Column space and null space live in the output/input spaces respectively and are orthogonal complements. Their dimensions always add up to n (Rank-Nullity theorem): rank + nullity = n.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — LU Decomposition
// Show A split into L and U with color coding
// ─────────────────────────────────────────────────────────────────
function DiagramLU() {
  const W = 880, H = 280;

  const A = [['2','1','1'],['4','3','3'],['8','7','9']];
  const L = [['1','0','0'],['2','1','0'],['4','3','1']];
  const U = [['2','1','1'],['0','1','1'],['0','0','2']];

  // Color: L lower triangle orange, U upper triangle blue
  const colL = [
    ['#d0e8ff', null, null],
    ['#ffd0d0','#d0e8ff', null],
    ['#ffd0d0','#ffd0d0','#d0e8ff'],
  ];
  const colU = [
    ['#d4f0d4','#d4f0d4','#d4f0d4'],
    [null,'#d4f0d4','#d4f0d4'],
    [null,null,'#d4f0d4'],
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>LU Decomposition — Factoring A into Lower × Upper Triangular</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* A */}
        <text x={96} y={30} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">A</text>
        <Mat data={A} x0={30} y0={44} cW={54} cH={42}/>

        {/* = */}
        <text x={222} y={138} textAnchor="middle" fontSize="26" fill="#888">=</text>

        {/* L */}
        <text x={346} y={30} textAnchor="middle" fontSize="15" fill="#c0392b" fontWeight="700">L  (lower)</text>
        <Mat data={L} x0={278} y0={44} cW={54} cH={42} colors={colL} color="#c0392b"/>

        {/* × */}
        <text x={468} y={138} textAnchor="middle" fontSize="26" fill="#888">×</text>

        {/* U */}
        <text x={590} y={30} textAnchor="middle" fontSize="15" fill="#27ae60" fontWeight="700">U  (upper)</text>
        <Mat data={U} x0={490} y0={44} cW={54} cH={42} colors={colU} color="#27ae60"/>

        {/* Legend */}
        <rect x={20} y={196} width={840} height={72} rx={5} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={440} y={215} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Why LU? — Solving Ax=b becomes two easy triangular solves
        </text>

        {/* Step 1 */}
        <rect x={36} y={224} width={14} height={14} rx={2} fill="#ffd0d0"/>
        <text x={56} y={235} fontSize="11.5" fill="#c0392b" fontWeight="700">Step 1: Solve Ly = b</text>
        <text x={56} y={249} fontSize="11" fill="#555">Forward substitution — top to bottom, trivial</text>

        {/* Step 2 */}
        <rect x={280} y={224} width={14} height={14} rx={2} fill="#d4f0d4"/>
        <text x={300} y={235} fontSize="11.5" fill="#27ae60" fontWeight="700">Step 2: Solve Ux = y</text>
        <text x={300} y={249} fontSize="11" fill="#555">Back substitution — bottom to top, trivial</text>

        {/* Key insight */}
        <text x={560} y={232} fontSize="11.5" fill="#1F3864" fontWeight="700">Key insight:</text>
        <text x={560} y={247} fontSize="11" fill="#555">Factor A once, solve many b vectors cheaply</text>
        <text x={560} y={260} fontSize="10.5" fill="#888">Used in every FEA solver and numpy.linalg</text>
      </svg>
      <div style={S.dCaption}>
        LU decomposition factors any square matrix into a lower triangular L (1s on diagonal, multipliers below) and upper triangular U (pivots on diagonal). Solving Ax=b then costs two fast triangular sweeps instead of a full elimination each time. This is what MATLAB's backslash and numpy.linalg.solve actually do internally.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Matrix as a Linear Transformation
// Show what a 2×2 matrix does to the unit square geometrically
// ─────────────────────────────────────────────────────────────────
function DiagramTransformation() {
  const W = 880, H = 300;
  const sc = 52;

  // Input unit square corners: (0,0),(1,0),(1,1),(0,1)
  // Transform A = [[2,1],[0.5,1.5]]
  // Output corners: A*(1,0)=(2,0.5), A*(0,1)=(1,1.5), A*(1,1)=(3,2)

  const Lox = 130, Loy = 190;  // left origin
  const Rox = 610, Roy = 190;  // right origin

  // Input unit square
  const inPts  = [[0,0],[1,0],[1,1],[0,1]];
  // Output transformed points
  const outPts = [[0,0],[2,0.5],[3,2],[1,1.5]];

  const toSVG = (ox, oy, [x, y]) => [ox + x*sc, oy - y*sc];

  const inPoly  = inPts.map(p  => toSVG(Lox, Loy, p).join(',')).join(' ');
  const outPoly = outPts.map(p => toSVG(Rox, Roy, p).join(',')).join(' ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Matrix as a Linear Transformation — What A Does to Space</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT: input space ── */}
        <rect x={10} y={8} width={360} height={280} rx={6} fill="#fff" stroke="#2E75B6" strokeWidth="1.6"/>
        <text x={190} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Input Space — Unit Square</text>

        {/* grid */}
        {[-1,0,1,2,3].map(v => (
          <g key={v}>
            <line x1={16} y1={Loy - v*sc} x2={362} y2={Loy - v*sc} stroke="#f0f0f0" strokeWidth="1"/>
            <line x1={Lox + v*sc} y1={14}  x2={Lox + v*sc} y2={278} stroke="#f0f0f0" strokeWidth="1"/>
          </g>
        ))}
        <line x1={16} y1={Loy} x2={362} y2={Loy} stroke="#bbb" strokeWidth="1.3"/>
        <line x1={Lox} y1={14} x2={Lox} y2={278} stroke="#bbb" strokeWidth="1.3"/>

        {/* unit square */}
        <polygon points={inPoly} fill="#d0e8ff" opacity="0.6" stroke="#2E75B6" strokeWidth="2"/>

        {/* basis vectors e1, e2 */}
        <line x1={Lox} y1={Loy} x2={Lox+sc-4} y2={Loy}
          stroke="#2E75B6" strokeWidth="2.5" markerEnd="url(#arr_e1)"/>
        <defs>
          <marker id="arr_e1" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0,7 3.5,0 7" fill="#2E75B6"/>
          </marker>
          <marker id="arr_e2" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0,7 3.5,0 7" fill="#27ae60"/>
          </marker>
          <marker id="arr_out1" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0,7 3.5,0 7" fill="#c0392b"/>
          </marker>
          <marker id="arr_out2" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0,7 3.5,0 7" fill="#9b59b6"/>
          </marker>
        </defs>
        <text x={Lox+sc/2} y={Loy+18} textAnchor="middle" fontSize="13" fill="#2E75B6" fontWeight="700">e₁=(1,0)</text>

        <line x1={Lox} y1={Loy} x2={Lox} y2={Loy-sc+4}
          stroke="#27ae60" strokeWidth="2.5" markerEnd="url(#arr_e2)"/>
        <text x={Lox+10} y={Loy-sc-6} fontSize="13" fill="#27ae60" fontWeight="700">e₂=(0,1)</text>

        {/* area label */}
        <text x={Lox+sc/2+6} y={Loy-sc/2} fontSize="12" fill="#1F3864">area=1</text>

        {/* ── Arrow in middle ── */}
        <text x={430} y={148} textAnchor="middle" fontSize="28" fill="#e07b00">→</text>
        <rect x={398} y={154} width={66} height={36} rx={4} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
        <text x={431} y={166} textAnchor="middle" fontSize="11" fill="#e07b00" fontWeight="700">Apply A</text>
        <text x={431} y={180} textAnchor="middle" fontSize="10.5" fill="#e07b00" fontFamily="'Courier New',monospace">[[2,1],[½,1½]]</text>

        {/* ── RIGHT: output space ── */}
        <rect x={500} y={8} width={372} height={280} rx={6} fill="#fff" stroke="#c0392b" strokeWidth="1.6"/>
        <text x={686} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Output Space — Transformed Parallelogram</text>

        {[-1,0,1,2,3].map(v => (
          <g key={v}>
            <line x1={506} y1={Roy - v*sc} x2={864} y2={Roy - v*sc} stroke="#f0f0f0" strokeWidth="1"/>
            <line x1={Rox + v*sc} y1={14}  x2={Rox + v*sc} y2={278} stroke="#f0f0f0" strokeWidth="1"/>
          </g>
        ))}
        <line x1={506} y1={Roy} x2={864} y2={Roy} stroke="#bbb" strokeWidth="1.3"/>
        <line x1={Rox} y1={14} x2={Rox} y2={278} stroke="#bbb" strokeWidth="1.3"/>

        {/* transformed shape */}
        <polygon points={outPoly} fill="#ffd0d0" opacity="0.55" stroke="#c0392b" strokeWidth="2"/>

        {/* Ae1 = first column of A = (2, 0.5) */}
        <line x1={Rox} y1={Roy} x2={Rox+2*sc-4} y2={Roy-0.5*sc}
          stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#arr_out1)"/>
        <text x={Rox+2*sc+6} y={Roy-0.5*sc+5} fontSize="12" fill="#c0392b" fontWeight="700">Ae₁=(2,½)</text>

        {/* Ae2 = second column of A = (1, 1.5) */}
        <line x1={Rox} y1={Roy} x2={Rox+1*sc-3} y2={Roy-1.5*sc}
          stroke="#9b59b6" strokeWidth="2.5" markerEnd="url(#arr_out2)"/>
        <text x={Rox+1*sc+6} y={Roy-1.5*sc+4} fontSize="12" fill="#9b59b6" fontWeight="700">Ae₂=(1,1½)</text>

        {/* area = |det A| */}
        <text x={Rox+1.4*sc} y={Roy-0.9*sc} fontSize="12" fill="#1F3864">
          area=|det A|
        </text>
        <text x={Rox+1.4*sc} y={Roy-0.9*sc+16} fontSize="12" fill="#1F3864">= |2.75|</text>
      </svg>
      <div style={S.dCaption}>
        Every matrix is a linear transformation. The columns of A tell you exactly where the standard basis vectors land. The unit square becomes a parallelogram — its new area equals |det(A)|. This is the geometric meaning of the determinant.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function MatrixOperations() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Matrix Operations</h1>

      <p style={S.p}>
        The Matrices page in Algebra covers the essentials — anatomy, multiplication, determinants, inverses, and eigenvalues. This page goes deeper into the operations that matter in linear algebra specifically: what the transpose really means, how rank measures the true information content of a matrix, the four fundamental subspaces every matrix creates, LU decomposition as the engine behind real solvers, and the geometric view of a matrix as a transformation of space.
      </p>

      <InfoBox
        title="What This Page Adds — Beyond the Algebra Matrices Page"
        items={[
          "Transpose: properties, symmetric matrices, and the reversal rule (AB)ᵀ = BᵀAᵀ",
          "Rank: row reduction, pivot counting, and what rank tells you about solutions",
          "Four fundamental subspaces: column space, null space, row space, left null space",
          "LU decomposition: how real solvers factor matrices for efficient computation",
          "Matrix as transformation: the geometric view — what a matrix actually does to space",
        ]}
      />

      {/* ── TRANSPOSE ── */}
      <h2 style={S.h2}>Transpose</h2>
      <DiagramTranspose />
      <Table2 rows={[
        ["Definition",           "Aᵀ: swap rows and columns — element (i,j) moves to position (j,i)\nm×n matrix becomes n×m matrix"],
        ["(AB)ᵀ = BᵀAᵀ",        "Order reverses on transpose — most common transpose mistake\nSame reversal rule as inverses: (AB)⁻¹ = B⁻¹A⁻¹"],
        ["Symmetric matrix",     "A = Aᵀ — element (i,j) equals element (j,i)\nStiffness matrices, covariance matrices, and stress tensors are always symmetric"],
        ["Skew-symmetric",       "A = −Aᵀ — diagonal must be zero\nCross-product matrices and angular velocity tensors are skew-symmetric"],
        ["Aᵀ A is always sym.",  "Aᵀ A is square, symmetric, and positive semi-definite\nAppears in least-squares: normal equations are (AᵀA)x = Aᵀb"],
        ["Orthogonal matrix",    "A matrix where Aᵀ = A⁻¹  →  AᵀA = I\nRotation matrices are orthogonal — transpose = inverse, very cheap to invert"],
      ]} />

      {/* ── RANK ── */}
      <h2 style={S.h2}>Rank and Row Reduction</h2>
      <DiagramRank />
      <Table2 rows={[
        ["Rank definition",      "rank(A) = number of linearly independent rows (= independent columns)\nThe true dimensionality of the information in the matrix"],
        ["Full rank",            "rank(A) = min(m,n) — no redundant rows or columns\nA square full-rank matrix is invertible"],
        ["Row reduction",        "Gaussian elimination: subtract row multiples to create zeros below pivots\nPivot count = rank"],
        ["Rank-Nullity theorem", "rank(A) + nullity(A) = n  (number of columns)\nIf rank=n, null space contains only the zero vector"],
        ["rank(A) = rank(Aᵀ)",   "Row rank always equals column rank — a non-obvious but fundamental fact\nYou can count independent rows or independent columns, same answer"],
        ["System solutions",     "Ax=b has a solution iff rank(A) = rank([A|b])\nUnique solution iff rank = n.  Infinite solutions iff rank < n."],
      ]} />

      {/* ── FOUR SUBSPACES ── */}
      <h2 style={S.h2}>The Four Fundamental Subspaces</h2>
      <DiagramFourSubspaces />
      <Table2 rows={[
        ["Column space C(A)",    "span of columns of A — all reachable output vectors Ax\ndim = rank(A),  lives in ℝᵐ"],
        ["Null space N(A)",      "all x where Ax = 0 — directions A collapses completely\ndim = n − rank,  lives in ℝⁿ  (Rank-Nullity)"],
        ["Row space C(Aᵀ)",      "span of rows of A — same dimension as column space\ndim = rank(A),  lives in ℝⁿ"],
        ["Left null space N(Aᵀ)","all y where Aᵀy = 0\ndim = m − rank,  lives in ℝᵐ"],
        ["Orthogonality",        "C(A) ⊥ N(Aᵀ)  and  C(Aᵀ) ⊥ N(A)\nEach pair is an orthogonal complement — together they fill the space"],
        ["Engineering use",      "Column space = what states are reachable in a control system\nNull space = what inputs produce no output — uncontrollable directions"],
      ]} />

      {/* ── LU DECOMPOSITION ── */}
      <h2 style={S.h2}>LU Decomposition</h2>
      <DiagramLU />
      <Table2 rows={[
        ["What it is",           "A = LU where L is lower triangular (1s on diagonal) and U is upper triangular\nL stores the row reduction multipliers, U is the row echelon form"],
        ["Why not just invert?", "Inverting an n×n matrix costs O(n³) and amplifies numerical errors\nLU solves Ax=b in O(n³) once, then each new b is O(n²) — much faster"],
        ["Forward substitution", "Solve Ly=b: start top row, substitute down — trivial because L is triangular\nEach unknown has only one unknown on its left"],
        ["Back substitution",    "Solve Ux=y: start bottom row, substitute up — trivial because U is triangular\nBuilt into every scientific computing library"],
        ["With pivoting",        "LU with partial pivoting: PA=LU where P is a permutation matrix\nPivoting improves numerical stability — default in practice"],
        ["Where you see it",     "numpy.linalg.solve(), MATLAB A\\b, ANSYS stiffness solver\nAll use LU (or Cholesky for symmetric positive definite) internally"],
      ]} />

      {/* ── MATRIX AS TRANSFORMATION ── */}
      <h2 style={S.h2}>Matrix as a Linear Transformation</h2>
      <DiagramTransformation />
      <Table2 rows={[
        ["Core idea",            "A matrix is not just a table of numbers — it is a function on vectors\nAx = apply transformation A to vector x"],
        ["Columns tell the story","Column j of A = where basis vector eⱼ lands after transformation\nKnowing where the basis goes determines where everything goes"],
        ["Determinant = area/vol","|det(A)| = how much A scales areas (2D) or volumes (3D)\ndet > 0: orientation preserved.  det < 0: orientation flipped.  det = 0: collapses space."],
        ["Composition",          "Apply A then B = matrix product BA  (note: B on the left)\nChaining transformations = multiplying matrices — order matters"],
        ["Rotation matrix",      "det = 1, columns orthonormal — pure rotation, no scaling\nInverse = transpose: A⁻¹ = Aᵀ — used constantly in robotics and CAD"],
        ["Projection matrix",    "P² = P — applying a projection twice is the same as once\nProjects onto column space of A:  P = A(AᵀA)⁻¹Aᵀ"],
      ]} />

      <InfoBox
        title="Key Takeaway — How These Concepts Connect"
        items={[
          "Rank tells you the true degrees of freedom in a matrix — how many independent directions it contains.",
          "The four subspaces partition input and output space cleanly — column space is what you can reach, null space is what you cannot distinguish.",
          "LU is how real computation is done — understanding it demystifies every black-box solver.",
          "The transformation view ties everything together: determinant = scale factor, columns = where basis lands, rank = dimension of the output.",
          "Next topic: Eigenvalues — the special input vectors that only scale under a transformation, never rotate.",
        ]}
      />
    </div>
  );
}